import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import MaskedInput from 'antd-mask-input';
import {
  ClientSvaReviewQuery,
  useClientSvaReviewQuery,
  useCompleteClientSvaReviewMutation,
  useApplyClientSvaReviewChangesMutation,
} from '../graphql/generated';
import { mbiValidationError, normalizeMBI } from '../utils/mbi';
import { tinValidationError } from '../utils/tin';
import useActiveParticipantTINValidation from '../hooks/useActiveParticipantTINValidation';
import DatePicker from '../components/DatePicker';
import ClientSVAPDFPreview from '../components/alignments/ClientSVAPDFPreview';
import {
  signatureDateValidationError,
  signatureDateToday,
} from '../utils/signatureDate';
import { DataChangeRequestValue } from '../components/dataChangeRequests/DataChangeRequestStatus';

type ClientSvaReviewRow = ClientSvaReviewQuery['pvas'][number];

const mbiFormatCharacters = {
  C: { validate: (character: string) => /^[1-9]$/.test(character) },
  A: {
    validate: (character: string) => /^[AC-HJKMNP-RT-Y]$/i.test(character),
    transform: (character: string) => character.toUpperCase(),
  },
  '#': {
    validate: (character: string) => /^[0-9AC-HJKMNP-RT-Y]$/i.test(character),
    transform: (character: string) => character.toUpperCase(),
  },
};

const displayDate = (value: string | null) =>
  value ? dayjs(value).format('MM/DD/YYYY') : '—';

const pageStyle: CSSProperties = { margin: '0 auto', maxWidth: 1440 };
const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};
const inlineEditInputStyle: CSSProperties = {
  background: 'transparent',
  marginLeft: -6,
  minWidth: 0,
  paddingLeft: 6,
  paddingRight: 6,
};
const pendingControlStyle: CSSProperties = {
  borderRadius: 4,
  outline: '2px solid #d4b106',
  outlineOffset: 1,
};
type EditableSvaField = 'mbi' | 'signature_date' | 'pva_tin';
const originalDraftField = {
  mbi: 'originalMbi',
  signature_date: 'originalSignatureDate',
  pva_tin: 'originalTin',
} as const;
type SvaDraft = {
  mbi?: string;
  signature_date?: string | null;
  pva_tin?: string;
  originalTin?: string | null;
  originalMbi?: string | null;
  originalSignatureDate?: string | null;
  updated_at: string;
};
const draftValidationError = (draft?: SvaDraft) =>
  draft &&
  ((draft.mbi !== undefined ? mbiValidationError(draft.mbi) : undefined) ||
    (draft.signature_date !== undefined
      ? signatureDateValidationError(draft.signature_date, false)
      : undefined) ||
    (draft.pva_tin !== undefined
      ? tinValidationError(draft.pva_tin)
      : undefined));

const ParticipantTinWarning: React.FC<{
  dcId?: string | null;
  tin: string;
  signatureDate?: string | null;
}> = ({ dcId, tin, signatureDate }) => {
  const { warning } = useActiveParticipantTINValidation({
    dcId,
    tin,
    signatureDate,
  });
  return warning ? (
    <div role="status" style={{ fontSize: 12, marginTop: 4 }}>
      <Typography.Text type="warning">
        <WarningOutlined /> {warning}
      </Typography.Text>
    </div>
  ) : null;
};

const ClientSVAReviewPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, SvaDraft>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [savingRow, setSavingRow] = useState<{
    id: string;
    action: 'apply' | 'complete';
  }>();
  const savingRef = useRef(false);
  const { data, loading, error, refetch } = useClientSvaReviewQuery({
    variables: { limit: pageSize, offset: (page - 1) * pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [
    completeSva,
    { loading: completing },
  ] = useCompleteClientSvaReviewMutation();
  const [
    applyChanges,
    { loading: applying },
  ] = useApplyClientSvaReviewChangesMutation();
  const busy = applying || completing || Boolean(savingRow);
  const total = data?.pvas_aggregate.aggregate?.count || 0;

  useEffect(() => {
    if (data && !loading) {
      const visibleIds = new Set(data.pvas.map((record) => record.pva_id));
      setExpandedRows((current) => {
        const next = current.filter((id) => visibleIds.has(id));
        return next.length === current.length ? current : next;
      });
    }
  }, [data, loading]);

  useEffect(() => {
    if (data && !loading) {
      setPage((current) =>
        Math.min(current, Math.max(1, Math.ceil(total / pageSize)))
      );
    }
  }, [data, loading, total, pageSize]);

  const clearError = (id: string) =>
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  const clearDraft = (id: string) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    clearError(id);
  };
  const revertField = (id: string, field: EditableSvaField) => {
    setDrafts((current) => {
      const next = { ...current };
      const draft = { ...next[id] };
      delete draft[field];
      delete draft[originalDraftField[field]];
      if (
        draft.mbi === undefined &&
        draft.signature_date === undefined &&
        draft.pva_tin === undefined
      )
        delete next[id];
      else next[id] = draft;
      return next;
    });
    clearError(id);
  };
  const setDraftField = (
    record: ClientSvaReviewRow,
    field: EditableSvaField,
    value: string | null
  ) => {
    const unchanged =
      field === 'mbi'
        ? normalizeMBI(value || '') === normalizeMBI(record.mbi || '')
        : field === 'pva_tin'
        ? value === (record.pva_tin || '')
        : value === (record.signature_date || null);
    if (unchanged) {
      revertField(record.pva_id, field);
      return;
    }
    setDrafts((current) => {
      const existing = current[record.pva_id];
      const draft: SvaDraft = {
        ...existing,
        updated_at: existing?.updated_at || record.updated_at,
      };
      if (field === 'mbi') {
        draft.mbi = value || '';
        draft.originalMbi =
          existing?.mbi !== undefined
            ? existing.originalMbi
            : record.mbi ?? null;
      } else if (field === 'pva_tin') {
        draft.pva_tin = value || '';
        draft.originalTin =
          existing?.pva_tin !== undefined
            ? existing.originalTin
            : record.pva_tin ?? null;
      } else {
        draft.signature_date = value;
        draft.originalSignatureDate =
          existing?.signature_date !== undefined
            ? existing.originalSignatureDate
            : record.signature_date ?? null;
      }
      return { ...current, [record.pva_id]: draft };
    });
    clearError(record.pva_id);
  };
  const saveRow = async (
    record: ClientSvaReviewRow,
    action: 'apply' | 'complete'
  ) => {
    const draft = drafts[record.pva_id];
    if (savingRef.current || loading) return;
    if (action === 'apply') {
      if (
        !draft ||
        draftValidationError(draft) ||
        draft.updated_at !== record.updated_at
      )
        return;
    } else if (
      draft ||
      mbiValidationError(record.mbi || '') ||
      signatureDateValidationError(record.signature_date) ||
      tinValidationError(record.pva_tin)
    )
      return;
    savingRef.current = true;
    setSavingRow({ id: record.pva_id, action });
    clearError(record.pva_id);
    try {
      const result =
        action === 'apply'
          ? await applyChanges({
              variables: {
                pva_id: record.pva_id,
                updated_at: draft!.updated_at,
                changes: {
                  ...(draft!.mbi !== undefined
                    ? { mbi: normalizeMBI(draft!.mbi) }
                    : {}),
                  ...(draft!.pva_tin !== undefined
                    ? { pva_tin: draft!.pva_tin }
                    : {}),
                  ...(draft!.signature_date !== undefined
                    ? { signature_date: draft!.signature_date }
                    : {}),
                },
              },
            })
          : await completeSva({
              variables: {
                pva_id: record.pva_id,
                updated_at: record.updated_at,
              },
            });
      if (result.data?.update_pvas?.affected_rows !== 1) {
        setSaveErrors((current) => ({
          ...current,
          [record.pva_id]:
            'This SVA changed or is no longer available. Revert any pending edit and review the refreshed row.',
        }));
      } else {
        clearDraft(record.pva_id);
        message.success(
          action === 'apply' ? 'Changes applied.' : 'SVA marked Completed.'
        );
      }
      try {
        await refetch();
      } catch {
        message.warning(
          'The table could not refresh. Refresh the page to see the latest SVAs.'
        );
      }
    } catch {
      setSaveErrors((current) => ({
        ...current,
        [record.pva_id]:
          action === 'apply'
            ? 'Unable to apply changes. Your pending edits are still available. Please try again.'
            : 'Unable to mark this SVA complete. Please try again.',
      }));
    } finally {
      savingRef.current = false;
      setSavingRow(undefined);
    }
  };

  const columns: ColumnsType<ClientSvaReviewRow> = [
    {
      title: 'ID',
      key: 'id',
      width: 350,
      render: (_, record) => {
        const submission = `${record.submission_source || '—'}, ${
          record.submission_id || '—'
        }`;
        return (
          <div>
            <Typography.Text
              strong
              style={{ display: 'block', whiteSpace: 'nowrap' }}
              ellipsis={{ tooltip: record.pva_id }}
            >
              {record.pva_id}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{
                display: 'block',
                fontSize: 12,
                marginTop: 4,
                whiteSpace: 'nowrap',
              }}
              ellipsis={{ tooltip: submission }}
            >
              {submission}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, marginTop: 4 }}
            >
              Created {dayjs(record.created_at).format('MMM D, YYYY h:mm A')}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: 'Transcriber Notes',
      dataIndex: 'transcriber_notes',
      width: 250,
      ellipsis: true,
      render: (notes: string | null) => (
        <Typography.Text type="danger">{notes}</Typography.Text>
      ),
    },
    {
      title: 'First Name',
      dataIndex: 'beneficiary_first_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Last Name',
      dataIndex: 'beneficiary_last_name',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'MBI',
      key: 'mbi',
      width: 200,
      render: (_, record) => {
        const draft = drafts[record.pva_id];
        const validationError =
          draft?.mbi !== undefined ? mbiValidationError(draft.mbi) : undefined;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MaskedInput
                aria-label={`MBI for ${record.pva_id}`}
                aria-invalid={Boolean(validationError)}
                className="editable-field"
                bordered={false}
                allowClear
                mask="CA#1A#1AA11"
                formatCharacters={mbiFormatCharacters}
                placeholder="Enter MBI"
                placeholderChar=" "
                autoComplete="off"
                disabled={busy}
                value={
                  draft?.mbi !== undefined
                    ? draft.mbi
                    : normalizeMBI(record.mbi || '')
                }
                onChange={(event) =>
                  setDraftField(record, 'mbi', event.target.value)
                }
                style={
                  draft?.mbi !== undefined
                    ? { ...inlineEditInputStyle, ...pendingControlStyle }
                    : inlineEditInputStyle
                }
              />
              {draft?.mbi !== undefined && (
                <Tooltip
                  title={`Revert to current MBI: ${record.mbi || 'empty'}`}
                >
                  <Button
                    aria-label={`Revert MBI for ${record.pva_id}`}
                    icon={<UndoOutlined />}
                    size="small"
                    style={{ marginLeft: 4 }}
                    disabled={busy}
                    onClick={() => revertField(record.pva_id, 'mbi')}
                  />
                </Tooltip>
              )}
            </div>
            {validationError && (
              <Typography.Text
                type="danger"
                style={{ display: 'block', fontSize: 12, marginTop: 4 }}
              >
                {validationError}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Birth Date',
      dataIndex: 'birth_date',
      width: 120,
      render: displayDate,
    },
    {
      title: 'Signature Date',
      key: 'signature_date',
      width: 200,
      render: (_, record) => {
        const draft = drafts[record.pva_id];
        const pending = draft?.signature_date !== undefined;
        const value = pending ? draft.signature_date : record.signature_date;
        const validationError = pending
          ? signatureDateValidationError(value, false)
          : undefined;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <DatePicker
                aria-label={`Signature Date for ${record.pva_id}`}
                aria-invalid={Boolean(validationError)}
                className="editable-field"
                bordered={false}
                allowClear
                format="MM/DD/YYYY"
                placeholder="Enter date"
                disabled={busy}
                value={value ? dayjs(value) : null}
                disabledDate={(date) =>
                  date.format('YYYY-MM-DD') > signatureDateToday()
                }
                onChange={(date) =>
                  setDraftField(
                    record,
                    'signature_date',
                    date ? date.format('YYYY-MM-DD') : null
                  )
                }
                style={
                  pending
                    ? { ...inlineEditInputStyle, ...pendingControlStyle }
                    : inlineEditInputStyle
                }
              />
              {pending && (
                <Tooltip
                  title={`Revert to current signature date: ${displayDate(
                    record.signature_date
                  )}`}
                >
                  <Button
                    aria-label={`Revert Signature Date for ${record.pva_id}`}
                    icon={<UndoOutlined />}
                    size="small"
                    style={{ marginLeft: 4 }}
                    disabled={busy}
                    onClick={() => revertField(record.pva_id, 'signature_date')}
                  />
                </Tooltip>
              )}
            </div>
            {validationError && (
              <Typography.Text
                type="danger"
                style={{ display: 'block', fontSize: 12, marginTop: 4 }}
              >
                {validationError}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'TIN',
      key: 'pva_tin',
      width: 180,
      render: (_, record) => {
        const draft = drafts[record.pva_id];
        const pending = draft?.pva_tin !== undefined;
        const validationError = pending
          ? tinValidationError(draft.pva_tin)
          : undefined;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MaskedInput
                aria-label={`TIN for ${record.pva_id}`}
                aria-invalid={Boolean(validationError)}
                className="editable-field"
                bordered={false}
                allowClear
                mask="111111111"
                placeholder="Enter TIN"
                placeholderChar=" "
                inputMode="numeric"
                autoComplete="off"
                disabled={busy}
                value={pending ? draft.pva_tin : record.pva_tin || ''}
                onChange={(event) =>
                  setDraftField(record, 'pva_tin', event.target.value)
                }
                style={
                  pending
                    ? { ...inlineEditInputStyle, ...pendingControlStyle }
                    : inlineEditInputStyle
                }
              />
              {pending && (
                <Tooltip
                  title={`Revert to current TIN: ${record.pva_tin || 'empty'}`}
                >
                  <Button
                    aria-label={`Revert TIN for ${record.pva_id}`}
                    icon={<UndoOutlined />}
                    size="small"
                    style={{ marginLeft: 4 }}
                    disabled={busy}
                    onClick={() => revertField(record.pva_id, 'pva_tin')}
                  />
                </Tooltip>
              )}
            </div>
            {validationError && (
              <Typography.Text
                type="danger"
                style={{ display: 'block', fontSize: 12, marginTop: 4 }}
              >
                {validationError}
              </Typography.Text>
            )}
            <ParticipantTinWarning
              dcId={record.network?.dc_id}
              tin={pending ? draft.pva_tin! : record.pva_tin || ''}
              signatureDate={
                draft?.signature_date !== undefined
                  ? draft.signature_date
                  : record.signature_date
              }
            />
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 310,
      fixed: 'right',
      render: (_, record) => {
        const draft = drafts[record.pva_id];
        const validationError = draftValidationError(draft);
        const savedMbiInvalid = Boolean(mbiValidationError(record.mbi || ''));
        const savedTinError = tinValidationError(record.pva_tin);
        const savedDateError = signatureDateValidationError(
          record.signature_date
        );
        const stale = draft && draft.updated_at !== record.updated_at;
        return (
          <Space
            direction="vertical"
            size={4}
            style={{ width: '100%', overflowWrap: 'anywhere' }}
          >
            {draft && (
              <div style={{ fontSize: 12 }}>
                <Typography.Text style={{ color: '#ad6800', display: 'block' }}>
                  Pending Changes
                </Typography.Text>
                {draft.mbi !== undefined && (
                  <div>
                    <Typography.Text type="secondary">MBI: </Typography.Text>
                    <DataChangeRequestValue
                      previous
                      value={draft.originalMbi}
                    />{' '}
                    <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                    <DataChangeRequestValue
                      value={
                        draft.mbi.trim()
                          ? draft.mbi.replace(/\s/g, '_').toUpperCase()
                          : ''
                      }
                    />
                  </div>
                )}
                {draft.pva_tin !== undefined && (
                  <div>
                    <Typography.Text type="secondary">TIN: </Typography.Text>
                    <DataChangeRequestValue
                      previous
                      value={draft.originalTin}
                    />{' '}
                    <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                    <DataChangeRequestValue
                      value={
                        draft.pva_tin.trim()
                          ? draft.pva_tin.replace(/\s/g, '_')
                          : ''
                      }
                    />
                  </div>
                )}
                {draft.signature_date !== undefined && (
                  <div>
                    <Typography.Text type="secondary">
                      Signature Date:{' '}
                    </Typography.Text>
                    <DataChangeRequestValue
                      previous
                      value={
                        draft.originalSignatureDate
                          ? displayDate(draft.originalSignatureDate)
                          : null
                      }
                    />{' '}
                    <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                    <DataChangeRequestValue
                      value={
                        draft.signature_date
                          ? displayDate(draft.signature_date)
                          : null
                      }
                    />
                  </div>
                )}
              </div>
            )}
            {(saveErrors[record.pva_id] || stale) && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {stale
                  ? 'This SVA changed. Revert your pending edit and review the refreshed row.'
                  : saveErrors[record.pva_id]}
              </Typography.Text>
            )}
            <Space size={8}>
              <Tooltip
                title={validationError && draft ? validationError : undefined}
              >
                <span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    disabled={
                      loading ||
                      busy ||
                      !draft ||
                      Boolean(validationError) ||
                      Boolean(stale)
                    }
                    loading={
                      savingRow?.id === record.pva_id &&
                      savingRow.action === 'apply'
                    }
                    onClick={() => saveRow(record, 'apply')}
                  >
                    Apply Changes
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  draft
                    ? 'Apply or revert pending changes before marking this SVA complete.'
                    : savedMbiInvalid
                    ? 'A valid saved MBI is required to mark this SVA complete.'
                    : savedTinError
                    ? savedTinError
                    : savedDateError
                    ? savedDateError
                    : undefined
                }
              >
                <span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    disabled={
                      loading ||
                      busy ||
                      Boolean(draft) ||
                      savedMbiInvalid ||
                      Boolean(savedDateError) ||
                      Boolean(savedTinError)
                    }
                    loading={
                      savingRow?.id === record.pva_id &&
                      savingRow.action === 'complete'
                    }
                    onClick={() => saveRow(record, 'complete')}
                  >
                    Mark Complete
                  </Button>
                </span>
              </Tooltip>
            </Space>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>SVA Review | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          SVA Review
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          Edit MBIs, Signature Dates, and TINs to address any problems described
          in the "Transcriber Notes." Once your changes are applied and the data
          is correct, click "Mark Complete" to finish.
        </Typography.Paragraph>
        {error && (
          <Alert
            type="error"
            showIcon
            message="Unable to load SVAs"
            action={<Button onClick={() => refetch()}>Retry</Button>}
            style={{ marginBottom: 16 }}
          />
        )}
        <Card bodyStyle={{ padding: 0 }} style={contentCardStyle}>
          <Table<ClientSvaReviewRow>
            rowKey="pva_id"
            size="small"
            columns={columns}
            dataSource={data?.pvas || []}
            loading={loading}
            tableLayout="fixed"
            expandable={{
              columnWidth: 48,
              expandedRowKeys: expandedRows,
              onExpand: (expanded, record) =>
                setExpandedRows((current) =>
                  expanded
                    ? [...current, record.pva_id]
                    : current.filter((id) => id !== record.pva_id)
                ),
              expandedRowRender: (record) =>
                expandedRows.includes(record.pva_id) ? (
                  <ClientSVAPDFPreview pvaId={record.pva_id} />
                ) : null,
            }}
            scroll={{
              x:
                48 +
                columns.reduce(
                  (width, column) => width + Number(column.width || 0),
                  0
                ),
            }}
            locale={{
              emptyText:
                'No SVAs are awaiting information in your organization’s networks.',
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100'],
              showTotal: (count) => `${count} SVAs awaiting information`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPageSize !== pageSize ? 1 : nextPage);
                setPageSize(nextPageSize || pageSize);
              },
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default ClientSVAReviewPage;
